/**
 * API Error Handling Utilities
 *
 * This module provides utilities for extracting user-friendly error messages
 * from various API error formats.
 */

import i18n from "@/i18n";

/**
 * API error response structure (FastAPI format)
 */
interface ApiErrorResponse {
  detail?: unknown;
  message?: string;
}

interface ValidationDetailItem {
  type?: unknown;
  loc?: unknown;
  msg?: unknown;
  ctx?: unknown;
}

/**
 * Type guard to check if a value is an object
 */
function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Type guard to check if a value is an Axios-style error
 */
function isAxiosError(value: unknown): value is {
  response?: { data?: ApiErrorResponse };
  message?: string;
} {
  if (value instanceof Error) {
    return false;
  }
  if (!isObject(value)) {
    return false;
  }
  return "response" in value || "message" in value;
}

/**
 * Extract detail from various formats
 *
 * Handles:
 * - String: direct return
 * - Array: FastAPI ValidationError format
 */
function extractDetail(detail: unknown): string | null {
  // String detail
  if (typeof detail === "string") {
    return detail;
  }

  // Object detail (structured error: { code, message, details })
  if (detail && typeof detail === "object") {
    const obj = detail as { message?: unknown };
    if (typeof obj.message === "string" && obj.message.trim()) {
      return obj.message;
    }
  }

  // Array detail (FastAPI ValidationError)
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string") {
      return first;
    }
    // ValidationError format: { loc: [...], msg: "...", type: "..." }
    if (first && typeof first === "object") {
      const validationMessage = formatValidationDetail(first as ValidationDetailItem);
      if (validationMessage) {
        return validationMessage;
      }
      if ("msg" in first && typeof first.msg === "string") {
        return first.msg;
      }
    }
  }

  return null;
}

function formatValidationDetail(detail: ValidationDetailItem): string | null {
  const loc = Array.isArray(detail.loc) ? detail.loc : [];
  const type = typeof detail.type === "string" ? detail.type : "";
  const ctx = isObject(detail.ctx) ? detail.ctx : {};
  const field = typeof loc.at(-1) === "string" ? loc.at(-1) : "";

  if (field === "search_query" && type === "string_too_long") {
    const maxLength =
      typeof ctx.max_length === "number"
        ? ctx.max_length
        : typeof ctx.maxLength === "number"
          ? ctx.maxLength
          : 1000;
    return i18n.t("pages:campaigns.editor.searchQueryTooLong", {
      maxLength,
      defaultValue: `ICP 搜索语句不能超过 ${maxLength} 个字符，请精简后再保存。`,
    });
  }

  if (field === "search_query" && type === "string_too_short") {
    const minLength =
      typeof ctx.min_length === "number"
        ? ctx.min_length
        : typeof ctx.minLength === "number"
          ? ctx.minLength
          : 10;
    return i18n.t("pages:campaigns.editor.searchQueryTooShort", {
      minLength,
      defaultValue: `ICP 搜索语句至少需要 ${minLength} 个字符，请补充后再保存。`,
    });
  }

  return null;
}

/**
 * Extract user-friendly error message from API response
 *
 * This function handles multiple error formats:
 * 1. Fetch format: { data: { detail: "..." }, status: 400 }
 * 2. Axios format: { response: { data: { detail: "..." } } }
 * 3. JSON Error: new Error(JSON.stringify({ detail: "..." }))
 * 4. FastAPI ValidationError: { detail: [{ msg: "..." }] }
 * 5. Plain Error: Error instance with message
 *
 * @param error - The error object from catch block
 * @param fallback - Fallback message if extraction fails
 * @returns User-friendly error message
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  // 1. Handle Fetch format (Orval-generated responses)
  // Format: { data: { detail: "..." }, status: 400, headers: {...} }
  if (isObject(error) && "data" in error) {
    const data = error.data as ApiErrorResponse | undefined;
    if (data) {
      const detail = extractDetail(data.detail);
      if (detail) return detail;
      if (typeof data.message === "string") return data.message;
    }
  }

  // 2. Handle Axios format
  // Format: { response: { data: { detail: "..." } } }
  if (isAxiosError(error)) {
    const data = error.response?.data as ApiErrorResponse | undefined;
    if (data) {
      const detail = extractDetail(data.detail);
      if (detail) return detail;
      if (typeof data.message === "string") return data.message;
    }
    const message = error.message;
    if (typeof message === "string" && message.trim()) {
      return message;
    }
  }

  // 3. Handle JSON Error (server functions throw these)
  // Format: new Error(JSON.stringify({ detail: "...", status: 400 }))
  if (error instanceof Error) {
    // Try to parse as JSON first
    try {
      const parsed = JSON.parse(error.message);
      if (parsed.detail) {
        const detail = extractDetail(parsed.detail);
        if (detail) return detail;
      }
      if (typeof parsed.message === "string") {
        return parsed.message;
      }
    } catch {
      // Not JSON, use raw message
    }

    // Return raw error message if not empty
    if (error.message.trim()) {
      return error.message;
    }
  }

  // 4. Fallback
  return fallback;
}

/**
 * Extract HTTP status code from error
 *
 * This function handles multiple error formats:
 * 1. Fetch format: { data: {...}, status: 401, headers: {...} }
 * 2. JSON Error: new Error(JSON.stringify({ detail: "...", status: 401 }))
 * 3. Error with status property: error.status = 401
 *
 * @param error - The error object from catch block
 * @returns HTTP status code or undefined if not found
 */
export function extractApiErrorStatus(error: unknown): number | undefined {
  // 1. Handle Fetch format (Orval-generated responses)
  // Format: { data: {...}, status: 401, headers: {...} }
  if (isObject(error) && "status" in error && typeof error.status === "number") {
    return error.status;
  }

  // 2. Handle JSON Error (server functions throw these)
  // Format: new Error(JSON.stringify({ detail: "...", status: 401 }))
  if (error instanceof Error) {
    try {
      const parsed = JSON.parse(error.message);
      if (typeof parsed.status === "number") {
        return parsed.status;
      }
    } catch {
      // Not JSON, ignore
    }
  }

  return undefined;
}

/**
 * Check if error is an authentication error (401 or 403)
 *
 * @param error - The error object from catch block
 * @returns true if error status is 401 or 403
 */
export function isAuthError(error: unknown): boolean {
  const status = extractApiErrorStatus(error);
  return status === 401 || status === 403;
}

export function isAutomationTaskLimitError(error: unknown): boolean {
  const status = extractApiErrorStatus(error);
  if (status !== 403) {
    return false;
  }

  const message = extractApiErrorMessage(error, "");
  return /active tasks?/i.test(message) && /upgrade your plan/i.test(message);
}

export interface AutomationTaskLimitDetails {
  plan: string;
  limit: number;
}

export function extractAutomationTaskLimitDetails(
  error: unknown,
): AutomationTaskLimitDetails | null {
  const message = extractApiErrorMessage(error, "");
  const match = message.match(/^\s*([A-Za-z][\w -]*)\s+plan supports\s+(\d+)\s+active tasks?/i);
  if (!match) {
    return null;
  }

  const limit = Number.parseInt(match[2] ?? "", 10);
  if (!Number.isFinite(limit)) {
    return null;
  }

  return {
    plan: match[1] ?? "Starter",
    limit,
  };
}
